import { html, attr, type Signal, Ensure } from '@tempots/dom'
import type { ActionDetail, CreditFormula } from '@/services/types'

export interface ActionPricingOptions {
  action: Signal<ActionDetail | null>
}

/**
 * Format a credit formula into a human-readable string.
 * Examples:
 * - "5 credits" (base only)
 * - "5 + 1/MB" (base + per MB)
 * - "3 + 0.5/page + 1/MB" (base + per page + per MB)
 */
export function formatCreditFormula(formula?: CreditFormula): string {
  if (!formula) return '0 credits'

  const parts: string[] = []

  // Base credits
  parts.push(`${formula.baseCredits}`)

  // Per page
  if (formula.creditsPerPage && formula.creditsPerPage > 0) {
    parts.push(`${formula.creditsPerPage}/page`)
  }

  // Per MB
  if (formula.creditsPerMB && formula.creditsPerMB > 0) {
    parts.push(`${formula.creditsPerMB}/MB`)
  }

  if (parts.length === 1) {
    return `${parts[0]} credits`
  }

  return parts.join(' + ') + ' credits'
}

export function ActionPricing({ action }: ActionPricingOptions) {
  return html.section(
    attr.class('bg-white rounded-xl border border-gray-200 p-6'),
    html.h2(
      attr.class('text-base font-semibold text-gray-900 mb-3'),
      'Pricing'
    ),
    html.div(
      attr.class('bg-gray-50 rounded-lg p-4'),
      // Human-readable formula
      html.p(
        attr.class(
          'text-lg font-mono text-gray-900 mb-3 flex flex-wrap items-center gap-1'
        ),
        action.map((a) => formatCreditFormula(a?.creditFormula))
      ),
      // Min/Max bounds if set
      Ensure(
        action.map((a) => {
          const formula = a?.creditFormula
          if (!formula) return null
          const { minCredits, maxCredits } = formula
          if (!minCredits && !maxCredits) return null
          return { min: minCredits, max: maxCredits }
        }),
        (bounds) =>
          html.p(
            attr.class('text-sm text-gray-500'),
            bounds.map(({ min, max }) => {
              if (min && max) return `Bounded: ${min} – ${max} credits`
              if (min) return `Minimum: ${min} credits`
              if (max) return `Maximum: ${max} credits`
              return ''
            })
          )
      )
    )
  )
}
